import {
  compose,
  withState,
  lifecycle,
  mapProps,
  withHandlers,
  withProps
} from 'recompose';
import omit from 'ramda/src/omit';
import propEq from 'ramda/src/propEq';
import * as configs from './config';
import * as db from './utils/firebaseUtils';
import { signInUser } from './utils/authUtils';
import * as analytics from './utils/analyticsUtils';
import { getDateFromToday, getWeek, getLanguage, getDistanceInKm } from './utils/helpers';
import { nonOptimalStates } from './components/hocs/helpers';
import Layout from './components/Layout';
import Loading from './components/Loading';
import Maintenance from './components/Maintenance';
import withAirportSuggests from './components/hocs/withAirportSuggests';
import withMaintenanceMode from './components/hocs/withMaintenanceMode';
import airports from './airports';

let destisCache = {};

const enhance = compose(
  withMaintenanceMode,
  withState('uid', 'setUid', ''),
  withState('departureDate', 'setDepartureDate', getDateFromToday(2)),
  withState('returnDate', 'setReturnDate', getDateFromToday(5)),
  withState('oneWay', 'setOneWay', false),
  withProps({ dates: getWeek(), language: getLanguage() }),
  withState('searches', 'setSearches', []),
  withState('destinations', 'setDestinations', null),
  withState('hasDirectOnly', 'setDirectOnly', false),
  withState('origin', 'setOrigin', ''),
  withState('originName', 'setOriginName', ''),
  withState('originCountryId', 'setOriginCountryId', 'ES'),
  withState('currency', 'setCurrency', 'EUR'),
  withState('degreeUnit', 'setDegreeUnit', 'C'),
  withState('filters', 'setFilters', {
    nonStop: false,
    minSunDays: 2
  }),
  withHandlers({
    toggleNonStopFilter: ({ setFilters, filters }) => () => {
      setFilters({ ...filters, nonStop: !filters.nonStop });
    },
    changeMinSunDays: ({ setFilters, filters }) => num => {
      setFilters({ ...filters, minSunDays: num });
    }
  }),
  mapProps(omit(['setFilters'])),
  withHandlers({
    fetchDestisWithFares: ({
      uid,
      currency,
      originCountryId,
      oneWay,
      departureDate,
      returnDate,
      setDestinations,
      setOrigin,
      setOriginName,
      setDirectOnly,
      changeMinSunDays
    }) => origin => {
      setDestinations(null);
      setOrigin(origin);
      setOriginName('');
      analytics.countSearch(uid, { origin, departureDate, returnDate, oneWay });
      const cacheKey = `${origin}-${departureDate}-${returnDate}}`;
      const cached = destisCache[cacheKey];
      if (cached) {
        setOrigin(cached.airport.iata);
        setOriginName(cached.airport.name);
        setDirectOnly(!cached.hasNonDirect);
        setDestinations(cached.destinations);
        return 0;
      }
      db.fetchDestisWithFares(
        origin,
        departureDate,
        returnDate,
        originCountryId,
        currency,
        oneWay,
        data => {
          const { destinations, airport } = data;
          const originAirport = airports.find(a => a.code === origin); // for coords
          // to show direct only toggle
          const hasNonDirect = !destinations
            ? false
            : !!destinations.filter(d => !d.quote.isDirect || false).length;

          setDirectOnly(!hasNonDirect);

          // filter weather data
          const weatherUtils = require('./utils/openWeatherUtils.js');
          const results = destinations
            .filter(d => d.quote.price < 1500) // temp until price toggle
            .filter(
              d => d.countryCode !== 'RE' && d.countryCode !== 'XK' // have no svg flags
            )
            .map(d => {
              const { coord } = d;
              return {
                ...d,
                distance: getDistanceInKm(
                  coord.lat,
                  coord.lon,
                  originAirport.lat,
                  originAirport.lon
                )
              };
            })
            .map(d => weatherUtils.withDateIndexes(d, departureDate, returnDate))
            .map(weatherUtils.withDateRange)
            .map(weatherUtils.withAvgs);
          // set state
          setOrigin(airport.iata);
          setOriginName(airport.name);
          let count = 0;
          results.forEach(r => {
            if (r.numSunDays > 3 && r.quote.price < 1500) {
              count++;
            }
          });
          if (count > 1) {
            changeMinSunDays(3);
          }
          setDestinations(results || []);
          // cache results
          const ts = Date.now();
          destisCache[cacheKey] = {
            ts,
            destinations: results,
            airport,
            hasNonDirect
          };
        }
      );
    }
  }),
  mapProps(omit(['setDestinations'])),
  withHandlers({
    syncUserData: ({ setUid, fetchDestinations }) => () => {
      configs.firebaseAuth().onAuthStateChanged(auth => {
        const uid = (auth && auth.uid) || '';
        setUid(uid);
        if (!uid) {
          signInUser();
        } else {
          analytics.countLogin(uid, window.location.hash);
        }
      });
    }
  }),
  mapProps(omit(['setUid'])),
  // routing
  withState('route', 'setRoute', { name: '', params: [] }),
  withHandlers({
    initRouter: ({ setRoute }) => () => {
      const handleHash = () => {
        const params = window.location.hash.replace(/^#\/?|\/$/g, '').split('/');
        setRoute({
          name: params[0],
          params: params.slice(1)
        });
      };
      window.onhashchange = handleHash;
      handleHash();
    }
  }),
  mapProps(omit(['setRoute'])),
  withAirportSuggests,
  withState('order', 'setOrder', 'price'),
  withHandlers({
    changeOrder: ({ setOrder, uid }) => order => {
      setOrder(order);
      analytics.countOrdering({ uid, order });
    }
  }),
  mapProps(omit(['setOrder'])),
  withHandlers({
    init: props => () => {
      props.syncMaintenanceMode();
      props.initRouter();
      props.syncUserData();
    }
  }),
  lifecycle({
    componentDidMount() {
      this.props.init();
    }
  }),
  // app is blocked until authed and has user profile with an origin
  nonOptimalStates([
    {
      when: propEq('uid', ''),
      render: Loading
    },
    {
      when: propEq('maintenanceMode', true),
      render: Maintenance
    }
  ]),
  mapProps(
    omit(['init', 'initRouter', 'maintenanceMode', 'syncMaintenanceMode', 'syncUserData'])
  )
);

export default enhance(Layout);



// WEBPACK FOOTER //
// ./src/App.js